import { Images } from '@/assets';
import CommonSearch from '@/components/search/CommonSearch';
import CommonTitle from '@/components/title/CommonTitle';
import { useLoading } from '@/context/LoadingContext';
import { useOrderQuery } from '@/hooks/router-query/useOrderQuery';
import { useAppSelector } from '@/stores/hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PlayerListItem from './list/PlayerListItem';
import { langPath } from '@/utils/langPath';
import styled from 'styled-components';
import { colors } from '@/theme/colors';
import { getPlayerList } from '@/api/player-list/getPlayerList';
import { player } from '@/stores/slices/player-list/playerSlice';
import Pagination from '@/components/pagination/Pagination';
import { KeywordItemType } from '@/api/type/commonTypes';
import { useTranslation } from 'react-i18next';
import { fonts } from '@/theme/fonts';

const MyPlayerListPage = () => {
  const { t } = useTranslation();
  const keywordTypeData: KeywordItemType[] = [
    {
      title: 'id',
      keyword: 'LOGIN_ID',
    },
    {
      title: 'name',
      keyword: 'NAME',
    },
    {
      title: 'clinic2',
      keyword: 'CLINIC_NAME',
    },
    {
      title: 'contact',
      keyword: 'CLINIC_CONTACT',
    },
    {
      title: 'address',
      keyword: 'ADDRESS',
    },
  ];
  const { page } = useParams<{ page?: string }>();
  const initialPage = page ? Number(page) : 1;
  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedKeywordType, setSelectedKeywordType] =
    useState<KeywordItemType>(keywordTypeData[0]);
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const keywordType = searchParams.get('keywordType');
  const { playerList } = useAppSelector((state) => state.player);
  const { startLoading, stopLoading } = useLoading();
  const { updateQuery } = useOrderQuery();
  const searchValueRef = useRef('');
  const navigate = useNavigate();

  /** 목록 fetch 함수 */
  const fetchPlayerList = useCallback(async () => {
    startLoading();
    try {
      const res = await getPlayerList({
        keywordType: keywordType ?? 'LOGIN_ID',
        keyword: searchValueRef.current,
        page: currentPage,
        size: 10,
      });
      if (res.isSuccess) {
        dispatch(player.setPlayerList(res.data));
      }
    } finally {
      stopLoading();
    }
  }, [currentPage, dispatch, keywordType, startLoading, stopLoading]);

  /** 페이지 변경 */
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    updateQuery({
      params: {
        keywordType: selectedKeywordType.keyword,
        keyword: searchValue,
        page: page,
        size: 10,
      },
      route: '/my-player-list',
    });
  };

  /** 검색 value 변경 */
  const onChangeSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchValue(value);
      searchValueRef.current = e.target.value;
    },
    []
  );

  /** 검색 아이콘 클릭 및 엔터 */
  const onClickSearch = useCallback(async () => {
    startLoading();
    try {
      const res = await getPlayerList({
        keywordType: selectedKeywordType.keyword,
        keyword: searchValueRef.current,
        page: currentPage,
        size: 10,
      });
      if (res.isSuccess) {
        dispatch(player.setPlayerList(res.data));
        updateQuery({
          params: {
            keywordType: selectedKeywordType.keyword,
            keyword: searchValueRef.current,
            page: currentPage,
            size: 10,
          },
          route: '/my-player-list',
        });
      }
    } finally {
      stopLoading();
    }
  }, [
    dispatch,
    selectedKeywordType.keyword,
    startLoading,
    stopLoading,
    updateQuery,
  ]);

  /** 검색 초기화 */
  const onClickSearchReset = useCallback(() => {
    setCurrentPage(1);
    setSearchValue('');
    searchValueRef.current = '';
    fetchPlayerList();
    navigate(langPath('/my-player-list'));
  }, [fetchPlayerList, navigate]);

  /** keywordType 저장 */
  const onDispatchKeyword = useCallback((keywordType: KeywordItemType) => {
    setSelectedKeywordType(keywordType);
  }, []);

  useEffect(() => {
    fetchPlayerList();
  }, [fetchPlayerList]);

  if (!playerList) return;

  return (
    <Container>
      <CommonTitle title={t('myPlayer')} imgUrl={Images.NavMyPlayerIcon} />
      <SearchWrap>
        <CommonSearch
          value={searchValue}
          onChange={onChangeSearch}
          onClickSearch={onClickSearch}
          onClickReset={onClickSearchReset}
          options={keywordTypeData}
          onSelect={onDispatchKeyword}
          selectedOption={selectedKeywordType}
        />
      </SearchWrap>
      <Count>Total: {`${playerList.totalElements} ${t('people')}`}</Count>
      <PlayerListItem items={playerList.items} />
      <Pagination
        totalItems={playerList.totalElements}
        itemsPerPage={10}
        currentPage={currentPage}
        onPageChange={handlePageChange}
      />
    </Container>
  );
};

export default MyPlayerListPage;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background: ${colors.white};
  width: 100%;
  box-sizing: border-box;
  height: 100%;
  padding: 43.5px 0 0 13px;
`;

const SearchWrap = styled.div`
  margin: 56.5px 0 45px;
`;

const Count = styled.div`
  padding: 0 0 12px 19px;
  font-family: ${fonts.family.poppins};
  font-size: 14px;
  line-height: 120%;
  color: #6a96f8;
  text-align: start;
`;
